import { Link, useLocation } from "react-router";
import { createLucideIcon } from "lucide-react";
import Images from "../utils/Images";
import { SITE, hashHref } from "../constants/site";

const Linkedin = createLucideIcon("linkedin", [
  ["path", { d: "M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" }],
  ["rect", { width: "4", height: "12", x: "2", y: "9" }],
  ["circle", { cx: "4", cy: "4", r: "2" }],
]);

const Facebook = createLucideIcon("facebook", [
  ["path", { d: "M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" }],
]);

const Instagram = createLucideIcon("instagram", [
  ["rect", { width: "20", height: "20", x: "2", y: "2", rx: "5", ry: "5" }],
  ["path", { d: "M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" }],
  ["line", { x1: "17.5", x2: "17.51", y1: "6.5", y2: "6.5" }],
]);

function XLogo({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.727-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const SOCIAL_ICONS = {
  LinkedIn: Linkedin,
  X: XLogo,
  Facebook,
  Instagram,
};

const QUICK_LINKS = [
  { label: "Home", href: hashHref("home") },
  { label: "About Us", href: "/about" },
  { label: "How it works", href: hashHref("how") },
  { label: "FAQs", href: "/faqs" },
  { label: "Request a Demo", href: hashHref("contact") },
  { label: "Become a Partner", href: hashHref("partner") },
];

const PLATFORM = [
  { label: "SimMarket", href: "/simmarkets" },
  { label: "SimVerify", href: "/simverify" },
  { label: "SimCollect", href: "/simcollect" },
];

const CONTACT_LINKS = [
  { label: "Request a demonstration", href: hashHref("contact") },
  { label: "Speak with a specialist", href: hashHref("callback") },
  { label: "Newsletter", href: hashHref("newsletter") },
];

const LEGAL_LINKS = [
  { label: "Legal", href: "/legal" },
  { label: "Privacy Policy", href: "/privacy" },
];

function Footer() {
  const { pathname } = useLocation();
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden border-t border-navy/10 bg-white text-navy" role="contentinfo">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_100%,rgb(var(--color-gold)/0.08),transparent_55%),radial-gradient(50%_40%_at_100%_0%,rgb(var(--color-navy)/0.05),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.28]"
          style={{
            backgroundImage: "radial-gradient(rgb(var(--color-navy) / 0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,2.2fr)] lg:gap-16">
          <div>
            <Link
              to={hashHref("home")}
              className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal focus-visible:ring-offset-2"
            >
              <img src={Images.logo} alt="SimFinance" className="h-9 w-auto sm:h-10" />
            </Link>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-navy/55">{SITE.tagline}</p>

            <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.22em] text-navy/40">
              Social media
            </p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {SITE.social.map((item) => (
                <li key={item.name}>
                  <SocialLink item={item} />
                </li>
              ))}
            </ul>
          </div>

          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <FooterList title="Quick links" items={QUICK_LINKS} pathname={pathname} />
            <FooterList title="Platform" items={PLATFORM} pathname={pathname} />
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-navy/40">
                Contact details
              </p>
              <ul className="mt-4 space-y-2.5">
                {SITE.email ? (
                  <li>
                    <a
                      href={`mailto:${SITE.email}`}
                      className="text-sm text-navy/65 transition-colors hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
                    >
                      {SITE.email}
                    </a>
                  </li>
                ) : null}
                {SITE.phone ? (
                  <li>
                    <a
                      href={`tel:${SITE.phone.replace(/\s+/g, "")}`}
                      className="text-sm text-navy/65 transition-colors hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
                    >
                      {SITE.phone}
                    </a>
                  </li>
                ) : null}
                {SITE.location ? (
                  <li className="text-sm text-navy/65">{SITE.location}</li>
                ) : null}
                {CONTACT_LINKS.map((item) => (
                  <li key={item.href}>
                    <FooterAnchor href={item.href} pathname={pathname}>
                      {item.label}
                    </FooterAnchor>
                  </li>
                ))}
              </ul>
            </div>
            <FooterList title="Legal" items={LEGAL_LINKS} pathname={pathname} />
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-navy/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-navy/40">
            © {year} {SITE.name}. All rights reserved.
          </p>
          <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
            {LEGAL_LINKS.map((item) => (
              <li key={item.href}>
                <FooterAnchor href={item.href} pathname={pathname}>
                  {item.label}
                </FooterAnchor>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}

function FooterList({ title, items, pathname }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-navy/40">{title}</p>
      <ul className="mt-4 space-y-2.5">
        {items.map((item) => (
          <li key={item.href}>
            <FooterAnchor href={item.href} pathname={pathname}>
              {item.label}
            </FooterAnchor>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FooterAnchor({ href, pathname, children }) {
  const active = pathname === href;
  const className = `text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal ${
    active ? "font-semibold text-navy" : "text-navy/65 hover:text-navy"
  }`;

  if (href.startsWith("/#") || href.startsWith("#")) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={className} aria-current={active ? "page" : undefined}>
      {children}
    </Link>
  );
}

function SocialLink({ item }) {
  const className =
    "inline-flex h-12 w-12 items-center justify-center border border-navy/15 text-navy/80 transition-colors hover:border-gold hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal";

  if (item.href) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={item.name}
        className={className}
      >
        <SocialIcon name={item.name} />
      </a>
    );
  }

  return (
    <a href={hashHref("contact")} aria-label={`${item.name} — contact us for this profile`} className={className}>
      <SocialIcon name={item.name} />
    </a>
  );
}

function SocialIcon({ name }) {
  const Icon = SOCIAL_ICONS[name] || XLogo;
  if (name === "X") {
    return <XLogo className="h-[1.35rem] w-[1.35rem]" />;
  }
  return <Icon className="h-6 w-6" strokeWidth={2} aria-hidden="true" />;
}

export default Footer;
